import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Stage, Layer, Rect, Circle, Line, Arrow, Text, Ring, Group, Path, Arc, Ellipse, Transformer } from 'react-konva';
import jsPDF from 'jspdf';
import { PitchToolbarTop, PitchToolbarBottom, PitchToolsDrawer, PitchSettingsSheet, TOKEN_TYPES, FIELD_BACKGROUNDS, PATH_TOOLS } from './PitchToolbar';
import PitchAnimator from './PitchAnimator';
import { toast } from '../../lib/toast';
import { Modal } from '../../pages/organization/finance/shared';

const BASE_W = 1000;
const TOKEN_R = 16;
const MAX_HISTORY = 50;
const DEFAULT_PATH_LEN = 18;

const emptyScene = () => ({
  schema_version: 1, field_template: 'full', orientation: 'vertical', elements: [],
  team_a_color: '#2563eb', team_b_color: '#dc2626', show_names: false, field_background: 'green', keyframes: [],
});

const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

function baseHeight(template, orientation) {
  const ratio = template === 'penalty_area' ? 0.55 : template === 'third' ? 0.55 : 0.65;
  const h = BASE_W * ratio;
  return orientation === 'vertical' ? BASE_W : h;
}
function baseWidth(orientation) {
  return orientation === 'vertical' ? baseHeight('full', 'horizontal') : BASE_W;
}

/* ملعب واقعي: عشب مقصوص بخطوط + علامات دقيقة (منطقة جزاء، منطقة الحارس، نقطة ضربة الجزاء وقوسها، أقواس الزوايا، المرمى) */
const FieldMarkings = ({ template, orientation, w, h, background }) => {
  const vertical = orientation === 'vertical';
  const bg = FIELD_BACKGROUNDS.find((b) => b.key === background) || FIELD_BACKGROUNDS[0];
  const lineColor = bg.line;
  const grass = bg.grass !== false;
  const stripe = bg.stripe;
  const lw = Math.max(1.8, Math.min(w, h) * 0.0032);

  // L = طول الملعب (محور الهجوم)، W = عرضه
  const pad = Math.min(w, h) * 0.045;
  const L = (vertical ? h : w) - pad * 2;
  const W = (vertical ? w : h) - pad * 2;
  // تحويل من إحداثيات الملعب (l على الطول، t على العرض) إلى إحداثيات الشاشة
  const X = (l, t) => (vertical ? pad + t : pad + l);
  const Y = (l, t) => (vertical ? pad + l : pad + t);
  const box = (l0, t0, l1, t1, extra = {}) => (
    <Rect
      x={Math.min(X(l0, t0), X(l1, t1))}
      y={Math.min(Y(l0, t0), Y(l1, t1))}
      width={Math.abs(X(l1, t1) - X(l0, t0))}
      height={Math.abs(Y(l1, t1) - Y(l0, t0))}
      stroke={lineColor}
      strokeWidth={lw}
      {...extra}
    />
  );

  const penD = L * 0.17;        // عمق منطقة الجزاء
  const penW = W * 0.62;        // عرض منطقة الجزاء
  const sixD = L * 0.058;       // عمق منطقة الحارس
  const sixW = W * 0.3;
  const spotD = L * 0.115;      // بُعد نقطة الجزاء
  const arcR = Math.min(L, W) * 0.115;
  const goalW = W * 0.19;
  const goalD = L * 0.022;
  const centerR = Math.min(L, W) * 0.145;
  const dotR = Math.max(2.4, lw * 1.3);
  const stripes = 10;
  const showFull = template === 'full' || template === 'third' || template === 'half';
  const showPen = template === 'penalty_area';

  const Ends = ({ both }) => {
    const ends = both ? [0, 1] : [0];
    return ends.map((e) => {
      const dir = e === 0 ? 1 : -1;
      const base = e === 0 ? 0 : L;
      const t0 = (W - penW) / 2;
      const s0 = (W - sixW) / 2;
      const spotL = base + dir * spotD;
      const awayAngle = vertical ? (e === 0 ? 90 : 270) : (e === 0 ? 0 : 180);
      return (
        <Group key={e}>
          {box(base, t0, base + dir * penD, t0 + penW)}
          {box(base, s0, base + dir * sixD, s0 + sixW)}
          <Circle x={X(spotL, W / 2)} y={Y(spotL, W / 2)} radius={dotR} fill={lineColor} />
          <Arc
            x={X(spotL, W / 2)}
            y={Y(spotL, W / 2)}
            innerRadius={arcR}
            outerRadius={arcR}
            angle={104}
            rotation={awayAngle - 52}
            stroke={lineColor}
            strokeWidth={lw}
          />
          {/* المرمى */}
          {box(base, (W - goalW) / 2, base - dir * goalD, (W - goalW) / 2 + goalW, { stroke: lineColor, strokeWidth: lw * 1.6, fill: 'rgba(255,255,255,0.14)' })}
          {/* أقواس الزوايا */}
          {[0, W].map((tc) => (
            <Arc
              key={tc}
              x={X(base, tc)}
              y={Y(base, tc)}
              innerRadius={Math.min(L, W) * 0.028}
              outerRadius={Math.min(L, W) * 0.028}
              angle={90}
              rotation={vertical
                ? (e === 0 ? (tc === 0 ? 0 : 90) : (tc === 0 ? 270 : 180))
                : (e === 0 ? (tc === 0 ? 0 : 270) : (tc === 0 ? 90 : 180))}
              stroke={lineColor}
              strokeWidth={lw * 0.85}
            />
          ))}
        </Group>
      );
    });
  };

  return (
    <Group listening={false}>
      <Rect x={0} y={0} width={w} height={h} fill={bg.fill} cornerRadius={6} />
      {grass && stripe && Array.from({ length: stripes }).map((_, i) => (i % 2 === 0 ? null : (
        <Rect
          key={i}
          x={vertical ? 0 : (w / stripes) * i}
          y={vertical ? (h / stripes) * i : 0}
          width={vertical ? w : w / stripes}
          height={vertical ? h / stripes : h}
          fill={stripe}
        />
      )))}
      {template !== 'blank' && (
        <>
          {box(0, 0, L, W, { strokeWidth: lw * 1.3 })}
          {showFull && (
            <>
              <Line points={[X(L / 2, 0), Y(L / 2, 0), X(L / 2, W), Y(L / 2, W)]} stroke={lineColor} strokeWidth={lw} />
              <Circle x={X(L / 2, W / 2)} y={Y(L / 2, W / 2)} radius={centerR} stroke={lineColor} strokeWidth={lw} />
              <Circle x={X(L / 2, W / 2)} y={Y(L / 2, W / 2)} radius={dotR} fill={lineColor} />
            </>
          )}
          {showFull && <Ends both />}
          {showPen && <Ends both={false} />}
        </>
      )}
    </Group>
  );
};

const SH = { shadowColor: 'rgba(8,15,25,0.45)', shadowBlur: 7, shadowOffsetY: 3, shadowOpacity: 0.85 };

const TokenShape = ({ el, teamColors, showNames }) => {
  const meta = TOKEN_TYPES.find((t) => t.type === el.type) || {};
  const color = el.color || (meta.team === 'a' ? teamColors.a : meta.team === 'b' ? teamColors.b : meta.color) || '#0f172a';
  const label = (el.text || meta.label || '').slice(0, 2);
  const showBelowName = showNames && el.playerName && (el.type === 'player_a' || el.type === 'player_b' || el.type === 'goalkeeper');
  switch (meta.shape) {
    case 'circle':
      return (
        <>
          <Circle radius={TOKEN_R * 1.7} fill="rgba(255,255,255,0.01)" />
          <Circle radius={TOKEN_R + 2.4} fill="#ffffff" {...SH} />
          <Circle
            radius={TOKEN_R}
            fillRadialGradientStartPoint={{ x: -TOKEN_R * 0.35, y: -TOKEN_R * 0.45 }}
            fillRadialGradientStartRadius={0}
            fillRadialGradientEndPoint={{ x: 0, y: 0 }}
            fillRadialGradientEndRadius={TOKEN_R * 1.25}
            fillRadialGradientColorStops={[0, 'rgba(255,255,255,0.55)', 0.42, color, 1, color]}
          />
          <Circle radius={TOKEN_R} stroke="rgba(0,0,0,0.22)" strokeWidth={1} />
          {el.type === 'goalkeeper' && <Circle radius={TOKEN_R - 4} stroke="#ffffff" strokeWidth={1.4} opacity={0.85} dash={[3, 2.4]} />}
          <Text text={label} fontSize={13.5} fontStyle="bold" fill="#fff" width={TOKEN_R * 2} x={-TOKEN_R} y={-7} align="center" shadowColor="rgba(0,0,0,0.45)" shadowBlur={2} />
          {showBelowName && (
            <>
              <Rect x={-38} y={TOKEN_R + 4} width={76} height={16} fill="rgba(255,255,255,0.92)" cornerRadius={8} />
              <Text text={el.playerName} fontSize={11} fill="#0f172a" width={76} x={-38} y={TOKEN_R + 7} align="center" fontStyle="bold" />
            </>
          )}
        </>
      );
    case 'ball':
      return (
        <>
          <Circle radius={TOKEN_R * 1.7} fill="rgba(255,255,255,0.01)" />
          <Circle
            radius={TOKEN_R * 0.82}
            fillRadialGradientStartPoint={{ x: -TOKEN_R * 0.3, y: -TOKEN_R * 0.35 }}
            fillRadialGradientStartRadius={0}
            fillRadialGradientEndPoint={{ x: 0, y: 0 }}
            fillRadialGradientEndRadius={TOKEN_R}
            fillRadialGradientColorStops={[0, '#ffffff', 0.7, '#f1f5f9', 1, '#cbd5e1']}
            stroke="#0f172a"
            strokeWidth={1.3}
            {...SH}
          />
          <Path data="M0,-5.6 L5.3,-1.7 L3.3,4.6 L-3.3,4.6 L-5.3,-1.7 Z" fill="#0f172a" />
          <Path data="M0,-5.6 L0,-9.5 M5.3,-1.7 L9,-3.6 M3.3,4.6 L5.4,8 M-3.3,4.6 L-5.4,8 M-5.3,-1.7 L-9,-3.6" stroke="#0f172a" strokeWidth={1.1} />
        </>
      );
    case 'square':
      return (
        <>
          <Circle radius={TOKEN_R * 1.6} fill="rgba(255,255,255,0.01)" />
          <Rect x={-TOKEN_R * 0.95} y={-TOKEN_R * 0.95} width={TOKEN_R * 1.9} height={TOKEN_R * 1.9} cornerRadius={4} fill={color} stroke="#fff" strokeWidth={2} {...SH} />
          <Text text={label} fontSize={12.5} fontStyle="bold" fill="#fff" width={TOKEN_R * 1.9} x={-TOKEN_R * 0.95} y={-6.5} align="center" />
        </>
      );
    case 'triangle':
      return (
        <>
          <Circle radius={TOKEN_R * 1.5} fill="rgba(255,255,255,0.01)" />
          <Path
            data={`M0,${-TOKEN_R * 1.15} L${TOKEN_R * 0.66},${TOKEN_R * 0.85} L${-TOKEN_R * 0.66},${TOKEN_R * 0.85} Z`}
            fillLinearGradientStartPoint={{ x: -TOKEN_R * 0.66, y: 0 }}
            fillLinearGradientEndPoint={{ x: TOKEN_R * 0.66, y: 0 }}
            fillLinearGradientColorStops={[0, color, 0.45, '#ffffff', 0.46, color, 1, '#9a3412']}
            {...SH}
          />
          <Rect x={-TOKEN_R * 0.42} y={TOKEN_R * 0.1} width={TOKEN_R * 0.84} height={TOKEN_R * 0.2} fill="#fff7ed" opacity={0.92} />
          <Rect x={-TOKEN_R * 0.82} y={TOKEN_R * 0.78} width={TOKEN_R * 1.64} height={TOKEN_R * 0.3} fill="#7c2d12" cornerRadius={1.5} />
        </>
      );
    case 'bar':
      return (
        <Group>
          <Circle radius={TOKEN_R * 1.5} fill="rgba(255,255,255,0.01)" />
          <Line points={[-TOKEN_R * 0.7, TOKEN_R * 0.9, -TOKEN_R * 0.7, -TOKEN_R * 0.1]} stroke={color} strokeWidth={2.6} lineCap="round" />
          <Line points={[TOKEN_R * 0.7, TOKEN_R * 0.9, TOKEN_R * 0.7, -TOKEN_R * 0.1]} stroke={color} strokeWidth={2.6} lineCap="round" />
          <Rect x={-TOKEN_R} y={-TOKEN_R * 0.38} width={TOKEN_R * 2} height={TOKEN_R * 0.34} fill={color} cornerRadius={2.5} {...SH} />
        </Group>
      );
    case 'pole':
      return (
        <>
          <Circle radius={TOKEN_R * 1.5} fill="rgba(255,255,255,0.01)" />
          <Rect x={-3} y={-TOKEN_R * 1.4} width={6} height={TOKEN_R * 2.8} cornerRadius={3} fill={color} {...SH} />
          <Rect x={-3} y={-TOKEN_R * 1.4} width={2} height={TOKEN_R * 2.8} fill="rgba(255,255,255,0.45)" cornerRadius={2} />
          <Ellipse radiusX={TOKEN_R * 0.55} radiusY={TOKEN_R * 0.2} y={TOKEN_R * 1.42} fill="rgba(0,0,0,0.25)" />
        </>
      );
    case 'ladder':
      return (
        <Group>
          <Circle radius={TOKEN_R * 1.6} fill="rgba(255,255,255,0.01)" />
          <Rect x={-TOKEN_R} y={-TOKEN_R * 1.4} width={TOKEN_R * 2} height={TOKEN_R * 2.8} stroke={color} strokeWidth={2.4} cornerRadius={2} {...SH} />
          {[-11, -3.5, 4, 11.5].map((yy) => <Line key={yy} points={[-TOKEN_R, yy, TOKEN_R, yy]} stroke={color} strokeWidth={2} />)}
        </Group>
      );
    case 'ring':
      return (
        <>
          <Circle radius={TOKEN_R * 1.5} fill="rgba(255,255,255,0.01)" />
          <Ring innerRadius={TOKEN_R * 0.58} outerRadius={TOKEN_R} fill={color} opacity={0.92} {...SH} />
          <Ring innerRadius={TOKEN_R * 0.86} outerRadius={TOKEN_R} fill="rgba(255,255,255,0.3)" />
        </>
      );
    case 'minigoal': {
      const gw = TOKEN_R * 2.1, gh = TOKEN_R * 1.2;
      return (
        <Group>
          <Circle radius={TOKEN_R * 1.7} fill="rgba(255,255,255,0.01)" />
          <Rect x={-gw / 2} y={-gh / 2} width={gw} height={gh} fill="rgba(255,255,255,0.18)" {...SH} />
          {[0.25, 0.5, 0.75].map((f) => <Line key={`v${f}`} points={[-gw / 2 + gw * f, -gh / 2, -gw / 2 + gw * f, gh / 2]} stroke="#ffffff" strokeWidth={0.8} opacity={0.7} />)}
          {[0.5].map((f) => <Line key={`h${f}`} points={[-gw / 2, -gh / 2 + gh * f, gw / 2, -gh / 2 + gh * f]} stroke="#ffffff" strokeWidth={0.8} opacity={0.7} />)}
          <Rect x={-gw / 2} y={-gh / 2} width={gw} height={gh} stroke={color} strokeWidth={2.4} />
        </Group>
      );
    }
    case 'goal': {
      const gw = TOKEN_R * 3.1, gh = TOKEN_R * 1.7;
      return (
        <Group>
          <Circle radius={TOKEN_R * 2.2} fill="rgba(255,255,255,0.01)" />
          <Rect x={-gw / 2} y={-gh / 2} width={gw} height={gh} fill="rgba(255,255,255,0.2)" {...SH} />
          {[0.166, 0.333, 0.5, 0.666, 0.833].map((f) => <Line key={`v${f}`} points={[-gw / 2 + gw * f, -gh / 2, -gw / 2 + gw * f, gh / 2]} stroke="#ffffff" strokeWidth={0.8} opacity={0.65} />)}
          {[0.33, 0.66].map((f) => <Line key={`h${f}`} points={[-gw / 2, -gh / 2 + gh * f, gw / 2, -gh / 2 + gh * f]} stroke="#ffffff" strokeWidth={0.8} opacity={0.65} />)}
          <Rect x={-gw / 2} y={-gh / 2} width={gw} height={gh} stroke={color} strokeWidth={2.8} />
        </Group>
      );
    }
    case 'dummy':
      return (
        <>
          <Circle radius={TOKEN_R * 1.6} fill="rgba(255,255,255,0.01)" />
          <Ellipse radiusX={TOKEN_R * 0.6} radiusY={TOKEN_R * 0.22} y={TOKEN_R * 1.15} fill="rgba(0,0,0,0.25)" />
          <Path data={`M0,${-TOKEN_R * 1.25} C${TOKEN_R * 0.75},${-TOKEN_R * 0.5} ${TOKEN_R * 0.6},${TOKEN_R * 0.55} ${TOKEN_R * 0.5},${TOKEN_R * 1.1} L${-TOKEN_R * 0.5},${TOKEN_R * 1.1} C${-TOKEN_R * 0.6},${TOKEN_R * 0.55} ${-TOKEN_R * 0.75},${-TOKEN_R * 0.5} 0,${-TOKEN_R * 1.25} Z`} fill={color} opacity={0.92} stroke="#ffffff" strokeWidth={1.2} {...SH} />
        </>
      );
    case 'zoneToken':
      return <><Circle radius={TOKEN_R * 1.6} fill="rgba(255,255,255,0.01)" /><Rect x={-TOKEN_R} y={-TOKEN_R} width={TOKEN_R * 2} height={TOKEN_R * 2} fill={color} opacity={0.35} stroke={color} strokeWidth={2} cornerRadius={5} dash={[5, 3]} /></>;
    case 'text':
      return (
        <>
          <Rect x={-6} y={-15} width={(el.text || 'نص').length * 11 + 14} height={30} fill="rgba(255,255,255,0.86)" cornerRadius={7} {...SH} />
          <Text text={el.text || 'نص'} fontSize={18} fill={color} fontStyle="bold" y={-9} x={2} />
        </>
      );
    case 'number':
      return (
        <>
          <Circle radius={TOKEN_R * 1.4} fill="rgba(255,255,255,0.01)" />
          <Circle radius={TOKEN_R * 0.92} fill="#ffffff" opacity={0.9} {...SH} />
          <Text text={el.text || '1'} fontSize={19} fill={color} fontStyle="bold" width={TOKEN_R * 2} x={-TOKEN_R} y={-10} align="center" />
        </>
      );
    default:
      return <Circle radius={TOKEN_R} fill={color} />;
  }
};

const isToken = (type) => TOKEN_TYPES.some((t) => t.type === type);
const EDITABLE_TOKEN_TYPES = ['text', 'number', 'player_a', 'player_b', 'goalkeeper', 'neutral'];
const PATH_STYLE = {
  arrow: { solid: true }, curved_arrow: { solid: true, curved: true }, line: { solid: true, line: true },
  dashed_line: { dash: true, line: true }, freehand: { line: true, free: true },
  circle: { circleShape: true }, rect: { rectShape: true }, zone_area: { rectShape: true, zone: true },
  movement_path: { solid: true }, pass_path: { dash: true }, dribble_path: { dash: true, wavy: true },
};
const ENDPOINT_EDITABLE = ['arrow', 'curved_arrow', 'line', 'dashed_line', 'movement_path', 'pass_path'];
const MOTION_PATHS = ['movement_path', 'dribble_path', 'pass_path', 'arrow', 'curved_arrow'];
const ANIMATABLE = ['player_a', 'player_b', 'goalkeeper', 'neutral', 'ball'];

const dist = (ax, ay, bx, by) => Math.hypot(ax - bx, ay - by);

// نقطة على مضلّع المسار عند نسبة t (0..1) حسب الطول الفعلي
const samplePolyline = (pts, t) => {
  const segs = [];
  let total = 0;
  for (let i = 0; i + 3 < pts.length; i += 2) {
    const d = dist(pts[i], pts[i + 1], pts[i + 2], pts[i + 3]);
    segs.push({ i, d });
    total += d;
  }
  if (!total) return { x: pts[0], y: pts[1] };
  let target = t * total;
  for (const s of segs) {
    if (target <= s.d || s === segs[segs.length - 1]) {
      const r = s.d ? target / s.d : 0;
      return {
        x: pts[s.i] + (pts[s.i + 2] - pts[s.i]) * r,
        y: pts[s.i + 1] + (pts[s.i + 3] - pts[s.i + 1]) * r,
      };
    }
    target -= s.d;
  }
  return { x: pts[pts.length - 2], y: pts[pts.length - 1] };
};

// يربط كل لاعب/كرة بأقرب مسار يبدأ عنده — لتحريك تلقائي من الأسهم المرسومة
const buildPathTracks = (elements) => {
  const paths = elements.filter((el) => MOTION_PATHS.includes(el.type) && (el.points || []).length >= 4);
  const used = new Set();
  const tracks = [];
  elements.filter((el) => ANIMATABLE.includes(el.type)).forEach((tok) => {
    let best = null;
    paths.forEach((p) => {
      if (used.has(p.id)) return;
      const d = dist(tok.x, tok.y, p.points[0], p.points[1]);
      if (d <= 9 && (!best || d < best.d)) best = { p, d };
    });
    if (best) { used.add(best.p.id); tracks.push({ id: tok.id, points: best.p.points }); }
  });
  return tracks;
};

function loadImageEl(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

function buildAutoFormation(minPlayers) {
  const n = Math.max(3, Math.min(11, Math.round(Number(minPlayers)) || 11));
  const hasGK = n >= 5;
  const outfield = hasGK ? n - 1 : n;
  const ratio = [4, 3, 3];
  const total = ratio.reduce((a, b) => a + b, 0);
  const lineCounts = ratio.map((r) => Math.max(1, Math.round((r / total) * outfield)));
  let diff = outfield - lineCounts.reduce((a, b) => a + b, 0);
  while (diff !== 0) {
    if (diff > 0) { lineCounts[lineCounts.length - 1] += 1; diff -= 1; }
    else { const idx = lineCounts.findIndex((c) => c > 1); if (idx === -1) break; lineCounts[idx] -= 1; diff += 1; }
  }
  return { hasGK, lineCounts };
}

const spreadAcross = (count) => {
  if (count === 1) return [50];
  const min = 18, max = 82;
  const step = (max - min) / (count - 1);
  return Array.from({ length: count }, (_, i) => min + step * i);
};

const PitchEditor = React.forwardRef(function PitchEditor({ value, onChange, readOnly, onSaved, rosterPlayers, tasyarIconUrl, orgLogoUrl, minPlayers }, ref) {
  const [scene, setScene] = useState(value && value.elements ? { ...emptyScene(), ...value } : emptyScene());
  const [tool, setTool] = useState('select');
  const [color, setColor] = useState('#0f172a');
  const [strokeWidth, setStrokeWidth] = useState(4);
  const [selectedIds, setSelectedIds] = useState([]);
  const zoom = 1;
  const stagePos = { x: 0, y: 0 };
  const [containerSize, setContainerSize] = useState({ w: 900, h: 585 });
  const [multiAdd] = useState(true);
  const [snap, setSnap] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [editingEl, setEditingEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const rootRef = useRef(null);
  const containerRef = useRef(null);
  const stageRef = useRef(null);
  const trRef = useRef(null);
  const nodeRefs = useRef({});
  const drawingRef = useRef(null);
  const historyRef = useRef({ stack: [scene.elements], index: 0 });
  const [historyState, setHistoryState] = useState({ canUndo: false, canRedo: false });

  useEffect(() => {
    const measure = () => {
      if (!containerRef.current) return;
      const wrapW = containerRef.current.clientWidth;
      const wrapH = containerRef.current.clientHeight;
      if (!wrapW || !wrapH) return;
      const bw_ = baseWidth(scene.orientation);
      const bh_ = baseHeight(scene.field_template, scene.orientation);
      const scale = Math.min(wrapW / bw_, wrapH / bh_);
      setContainerSize({ w: bw_ * scale, h: bh_ * scale });
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (containerRef.current) ro.observe(containerRef.current);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
  }, [scene.field_template, scene.orientation, fullscreen]);

  useEffect(() => {
    const onFsChange = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('tasyar:hide-chat-widget'));
    return () => window.dispatchEvent(new CustomEvent('tasyar:show-chat-widget'));
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) rootRef.current?.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  const bw = baseWidth(scene.orientation);
  const bh = baseHeight(scene.field_template, scene.orientation);
  const px = (percentVal) => (percentVal / 100) * bw;
  const py = (percentVal) => (percentVal / 100) * bh;
  const toPercentX = (v) => (v / bw) * 100;
  const toPercentY = (v) => (v / bh) * 100;
  const fitScale = containerSize.w / bw || 1;
  const displayScale = fitScale;

  const pushHistory = useCallback((elements) => {
    const h = historyRef.current;
    const newStack = h.stack.slice(0, h.index + 1);
    newStack.push(elements);
    if (newStack.length > MAX_HISTORY) newStack.shift();
    historyRef.current = { stack: newStack, index: newStack.length - 1 };
    setHistoryState({ canUndo: historyRef.current.index > 0, canRedo: false });
  }, []);

  const commitElements = useCallback((elements, record = true) => {
    setScene((s) => ({ ...s, elements }));
    if (record) pushHistory(elements);
  }, [pushHistory]);

  useEffect(() => {
    if (trRef.current) {
      const nodes = selectedIds.map((id) => nodeRefs.current[id]).filter(Boolean);
      trRef.current.nodes(nodes);
      trRef.current.getLayer()?.batchDraw();
    }
  }, [selectedIds, scene.elements]);

  const viewportCenterPercent = () => {
    const cx = containerSize.w / 2, cy = containerSize.h / 2;
    const stageX = (cx - stagePos.x) / (displayScale * zoom);
    const stageY = (cy - stagePos.y) / (displayScale * zoom);
    return { x: toPercentX(stageX), y: toPercentY(stageY) };
  };

  const nextNumberFor = (type) => scene.elements.filter((e) => e.type === type).length + 1;

  const insertElement = (type, multi) => {
    if (readOnly) return;
    const meta = TOKEN_TYPES.find((t) => t.type === type);
    const isPathType = PATH_STYLE[type];
    const existingCount = scene.elements.filter((e) => e.type === type).length;
    const offset = (existingCount % 6) * 3;
    const center = viewportCenterPercent();
    let newEl;
    if (isPathType) {
      const style = PATH_STYLE[type];
      const cx = Math.min(90, center.x + offset), cy = Math.min(90, center.y + offset);
      if (style.circleShape) {
        newEl = { id: uid(), type, color, strokeWidth, x: cx, y: cy, radius: 6 };
      } else if (style.rectShape) {
        newEl = { id: uid(), type, color, strokeWidth, x: Math.max(2, cx - 8), y: Math.max(2, cy - 6), width: 16, height: 12 };
      } else {
        const half = DEFAULT_PATH_LEN / 2;
        newEl = { id: uid(), type, color, strokeWidth, points: [Math.max(2, cx - half), cy, Math.min(98, cx + half), cy] };
      }
    } else {
      const meta2 = meta || {};
      const text = meta2.shape === 'number' ? String(nextNumberFor('number'))
        : (type === 'player_a' || type === 'player_b') ? String(nextNumberFor(type)) : '';
      newEl = { id: uid(), type, x: Math.min(92, center.x + offset), y: Math.min(92, center.y + offset), rotation: 0, color: meta2.color, text };
    }
    commitElements([...scene.elements, newEl]);
    setSelectedIds([newEl.id]);
    if (multi) setTool(type); else setTool('select');
    if (meta?.shape === 'text' || meta?.shape === 'number') {
      setTimeout(() => setEditingEl(newEl.id), 50);
    }
  };

  const relPointerPercent = () => {
    const stage = stageRef.current;
    const pos = stage.getRelativePointerPosition();
    return { x: toPercentX(pos.x), y: toPercentY(pos.y) };
  };

  const handleStageMouseDown = (e) => {
    if (readOnly) return;
    const style = PATH_STYLE[tool];
    if (style && !style.circleShape && !style.rectShape) {
      const { x, y } = relPointerPercent();
      drawingRef.current = { type: tool, startX: x, startY: y, points: [x, y] };
      return;
    }
    if (e.target === stageRef.current) setSelectedIds([]);
  };

  const handleStageMouseMove = () => {
    if (!drawingRef.current) return;
    const { x, y } = relPointerPercent();
    const d = drawingRef.current;
    if (PATH_STYLE[d.type]?.free) {
      d.points.push(x, y);
    } else {
      d.points = [d.startX, d.startY, x, y];
    }
    d.previewEnd = { x, y };
    setScene((s) => ({ ...s }));
  };

  const buildPathElement = (d) => {
    const style = PATH_STYLE[d.type];
    const base = { id: uid(), type: d.type, color, strokeWidth, locked: false };
    if (style.wavy) {
      const steps = 8;
      const pts = [];
      const dx = d.previewEnd.x - d.startX, dy = d.previewEnd.y - d.startY;
      const len = Math.sqrt(dx * dx + dy * dy) || 1;
      const nx = -dy / len, ny = dx / len;
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const offset = (i % 2 === 0 ? 1 : -1) * 2.2 * (i > 0 && i < steps ? 1 : 0);
        pts.push(d.startX + dx * t + nx * offset, d.startY + dy * t + ny * offset);
      }
      return { ...base, points: pts };
    }
    return { ...base, points: d.points.slice() };
  };

  const handleStageMouseUp = () => {
    if (!drawingRef.current) return;
    const d = drawingRef.current;
    drawingRef.current = null;
    if (PATH_STYLE[d.type]?.free) {
      if (d.points.length < 4) return;
    } else if (!d.previewEnd) {
      const ex = Math.min(98, d.startX + DEFAULT_PATH_LEN);
      d.previewEnd = { x: ex, y: d.startY };
      d.points = [d.startX, d.startY, ex, d.startY];
    }
    const el = buildPathElement(d);
    commitElements([...scene.elements, el]);
    setSelectedIds([el.id]);
    if (!multiAdd) setTool('select');
  };

  const updateElement = (id, patch) => {
    const elements = scene.elements.map((el) => (el.id === id ? { ...el, ...patch } : el));
    commitElements(elements);
  };

  const removeElement = (id) => commitElements(scene.elements.filter((el) => el.id !== id));

  const snapVal = (v) => (snap ? Math.round(v / 2) * 2 : v);

  const handleDragMove = (el, node) => {
    if (!el.groupId) return;
    const dx = node.x() - px(el.x), dy = node.y() - py(el.y);
    scene.elements.filter((o) => o.groupId === el.groupId && o.id !== el.id).forEach((o) => {
      const r = nodeRefs.current[o.id];
      if (r) r.position({ x: px(o.x) + dx, y: py(o.y) + dy });
    });
  };

  const clampPercent = (v) => Math.max(0, Math.min(100, v));

  const handleDragEnd = (el, node) => {
    const nx = snapVal(clampPercent(toPercentX(node.x()))), ny = snapVal(clampPercent(toPercentY(node.y())));
    node.position({ x: px(nx), y: py(ny) });
    const dx = nx - el.x, dy = ny - el.y;
    let elements = scene.elements.map((o) => (o.id === el.id ? { ...o, x: nx, y: ny } : o));
    if (el.groupId) {
      elements = elements.map((o) => (o.groupId === el.groupId && o.id !== el.id ? { ...o, x: clampPercent(o.x + dx), y: clampPercent(o.y + dy) } : o));
    }
    commitElements(elements);
  };

  const undo = () => {
    const h = historyRef.current;
    if (h.index <= 0) return;
    h.index -= 1;
    setScene((s) => ({ ...s, elements: h.stack[h.index] }));
    setHistoryState({ canUndo: h.index > 0, canRedo: true });
  };
  const redo = () => {
    const h = historyRef.current;
    if (h.index >= h.stack.length - 1) return;
    h.index += 1;
    setScene((s) => ({ ...s, elements: h.stack[h.index] }));
    setHistoryState({ canUndo: true, canRedo: h.index < h.stack.length - 1 });
  };

  const deleteSelected = () => { commitElements(scene.elements.filter((el) => !selectedIds.includes(el.id))); setSelectedIds([]); };
  const duplicateSelected = () => {
    const copies = scene.elements.filter((el) => selectedIds.includes(el.id)).map((el) => ({ ...el, id: uid(), x: clampPercent((el.x || 0) + 3), y: clampPercent((el.y || 0) + 3), groupId: null }));
    commitElements([...scene.elements, ...copies]);
    setSelectedIds(copies.map((c) => c.id));
  };
  const lockSelected = () => commitElements(scene.elements.map((el) => (selectedIds.includes(el.id) ? { ...el, locked: !el.locked } : el)));
  const bringFront = () => {
    const sel = scene.elements.filter((el) => selectedIds.includes(el.id));
    const rest = scene.elements.filter((el) => !selectedIds.includes(el.id));
    commitElements([...rest, ...sel]);
  };
  const sendBack = () => {
    const sel = scene.elements.filter((el) => selectedIds.includes(el.id));
    const rest = scene.elements.filter((el) => !selectedIds.includes(el.id));
    commitElements([...sel, ...rest]);
  };
  const groupSelected = () => {
    if (selectedIds.length < 2) return;
    const gid = uid();
    commitElements(scene.elements.map((el) => (selectedIds.includes(el.id) ? { ...el, groupId: gid } : el)));
  };
  const ungroupSelected = () => commitElements(scene.elements.map((el) => (selectedIds.includes(el.id) ? { ...el, groupId: null } : el)));

  const handleAutoFormation = () => {
    if (readOnly) return;
    const { hasGK, lineCounts } = buildAutoFormation(minPlayers);
    const vertical = scene.orientation !== 'horizontal';
    const depths = hasGK ? [92, 72, 48, 24] : [72, 48, 24];
    const gkColor = TOKEN_TYPES.find((t) => t.type === 'goalkeeper')?.color;
    const playerColor = TOKEN_TYPES.find((t) => t.type === 'player_a')?.color;
    const newPlayers = [];
    let seq = 1;
    if (hasGK) {
      const d = depths[0];
      newPlayers.push({ id: uid(), type: 'goalkeeper', x: vertical ? 50 : d, y: vertical ? d : 50, rotation: 0, color: gkColor, text: '' });
    }
    const lineDepths = hasGK ? depths.slice(1) : depths;
    lineCounts.forEach((count, li) => {
      const d = lineDepths[li];
      spreadAcross(count).forEach((p) => {
        newPlayers.push({
          id: uid(), type: 'player_a', x: vertical ? p : d, y: vertical ? d : p,
          rotation: 0, color: playerColor, text: String(seq),
        });
        seq += 1;
      });
    });
    const remaining = scene.elements.filter((el) => el.type !== 'player_a' && el.type !== 'goalkeeper');
    commitElements([...remaining, ...newPlayers]);
    setSelectedIds([]);
    toast.success(`تم توزيع ${newPlayers.length} لاعباً بتشكيلة تلقائية — يمكنك سحبها وتعديلها بحرية`);
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (readOnly) return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (!containerRef.current?.closest('[data-testid="pitch-editor-root"]')?.contains(document.activeElement) && document.activeElement !== document.body) return;
      if (e.key === 'Delete' || e.key === 'Backspace') { if (selectedIds.length) { e.preventDefault(); deleteSelected(); } }
      else if (e.ctrlKey && e.key === 'z') { e.preventDefault(); undo(); }
      else if (e.ctrlKey && (e.key === 'y' || (e.shiftKey && e.key === 'Z'))) { e.preventDefault(); redo(); }
      else if (e.ctrlKey && e.key === 'd') { e.preventDefault(); if (selectedIds.length) duplicateSelected(); }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedIds, scene.elements]);






  const drawSignature = async (ctx, w, h) => {
    const size = Math.max(26, w * 0.045);
    let xCursor = 10;
    try {
      if (tasyarIconUrl) {
        const icon = await loadImageEl(tasyarIconUrl);
        ctx.globalAlpha = 0.9;
        ctx.drawImage(icon, xCursor, h - size - 10, size, size);
        ctx.globalAlpha = 1;
        xCursor += size + 6;
      }
    } catch { /* signature is best-effort */ }
    try {
      if (orgLogoUrl) {
        const org = await loadImageEl(orgLogoUrl);
        ctx.globalAlpha = 0.9;
        ctx.drawImage(org, xCursor, h - size - 10, size, size);
        ctx.globalAlpha = 1;
      }
    } catch { /* signature is best-effort */ }
  };

  const getDataUrl = async () => {
    const stage = stageRef.current;
    const prevScale = { x: stage.scaleX(), y: stage.scaleY() }, prevPos = stage.position();
    stage.scale({ x: 1, y: 1 }); stage.position({ x: 0, y: 0 });
    const baseUri = stage.toDataURL({ pixelRatio: 2, width: bw, height: bh });
    stage.scale(prevScale); stage.position(prevPos);
    const canvas = document.createElement('canvas');
    canvas.width = bw * 2; canvas.height = bh * 2;
    const ctx = canvas.getContext('2d');
    const baseImg = await loadImageEl(baseUri);
    ctx.drawImage(baseImg, 0, 0, canvas.width, canvas.height);
    await drawSignature(ctx, canvas.width, canvas.height);
    return canvas.toDataURL('image/png');
  };

  const exportPng = async () => {
    const uri = await getDataUrl();
    const link = document.createElement('a');
    link.download = 'رسم-تدريبي.png';
    link.href = uri;
    link.click();
  };

  const exportPdf = async () => {
    const uri = await getDataUrl();
    const pdf = new jsPDF({ orientation: scene.orientation === 'vertical' ? 'portrait' : 'landscape', unit: 'pt' });
    const pageW = pdf.internal.pageSize.getWidth() - 40;
    const pageH = (pageW * bh) / bw;
    pdf.addImage(uri, 'PNG', 20, 20, pageW, pageH);
    pdf.save('رسم-تدريبي.pdf');
  };

  const handlePrint = async () => {
    const uri = await getDataUrl();
    const w = window.open('');
    w.document.write(`<html dir="rtl"><body style="margin:0;display:flex;justify-content:center;align-items:center;"><img src="${uri}" style="max-width:100%" onload="window.print();window.close();"/></body></html>`);
  };

  const save = async () => {
    setSaveStatus('saving');
    try {
      const previewUri = await getDataUrl();
      await onChange?.(scene, previewUri);
      onSaved?.();
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch {
      setSaveStatus('idle');
      toast.error('تعذر حفظ الرسم');
    }
  };

  /* ----------------- محرّك التحريك: مشاهد (Keyframes) + مسارات مرسومة ----------------- */
  const frames = scene.keyframes || [];
  const [activeFrame, setActiveFrame] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [loop, setLoop] = useState(true);
  const [animPos, setAnimPos] = useState(null);
  const rafRef = useRef(null);

  const capturePositions = () => {
    const pos = {};
    scene.elements.filter((el) => ANIMATABLE.includes(el.type)).forEach((el) => { pos[el.id] = { x: el.x, y: el.y }; });
    return pos;
  };

  const setFrames = (next) => setScene((s) => ({ ...s, keyframes: next }));

  const addFrame = () => {
    const pos = capturePositions();
    const f = { id: uid(), positions: pos, duration_ms: 1500 };
    setScene((s) => ({ ...s, anim_start: s.anim_start || pos, keyframes: [...(s.keyframes || []), f] }));
    setActiveFrame(frames.length + 1);
    toast.success(`أُضيف المشهد ${frames.length + 1} — حرّك اللاعبين لمواضعهم الجديدة ثم أضف مشهداً آخر`);
  };

  const deleteFrame = (idx) => {
    const next = frames.filter((_, i) => i !== idx);
    setScene((s) => ({ ...s, keyframes: next, anim_start: next.length ? s.anim_start : null }));
    setActiveFrame(0);
  };

  const updateFrame = (idx, patch) => setFrames(frames.map((f, i) => (i === idx ? { ...f, ...patch } : f)));

  // عند اختيار مشهد: نعرض مواضعه على الملعب فعلياً ليتمكن المدرب من تعديله
  const selectFrame = (idx) => {
    stopAnim();
    setActiveFrame(idx);
    const pos = idx === 0 ? scene.anim_start : frames[idx - 1]?.positions;
    if (!pos) return;
    commitElements(scene.elements.map((el) => (pos[el.id] ? { ...el, ...pos[el.id] } : el)));
  };

  // عند سحب عنصر ومشهد محدد: نحدّث مواضع المشهد نفسه
  useEffect(() => {
    if (playing || !frames.length) return;
    const ref = activeFrame === 0 ? scene.anim_start : frames[activeFrame - 1]?.positions;
    if (!ref) return;
    const pos = capturePositions();
    const changed = Object.keys(pos).some((k) => !ref[k] || ref[k].x !== pos[k].x || ref[k].y !== pos[k].y);
    if (!changed) return;
    if (activeFrame === 0) setScene((s) => ({ ...s, anim_start: pos }));
    else updateFrame(activeFrame - 1, { positions: pos });
    // eslint-disable-next-line
  }, [scene.elements]);

  const pathTracks = buildPathTracks(scene.elements);
  const canAnimate = frames.length > 0 || pathTracks.length > 0;

  const buildTimeline = () => {
    const base = capturePositions();
    if (frames.length) {
      const stops = [{ positions: scene.anim_start || base, duration_ms: 0 },
        ...frames.map((f) => ({ positions: f.positions, duration_ms: f.duration_ms || 1500 }))];
      const total = stops.slice(1).reduce((a, s) => a + s.duration_ms, 0) || 1500;
      return { base, stops, tracks: null, total };
    }
    if (pathTracks.length) return { base, stops: null, tracks: pathTracks, total: 2400 };
    return null;
  };

  const positionsAt = (tl, t) => {
    const out = {};
    if (tl.stops) {
      const target = Math.min(1, Math.max(0, t)) * tl.total;
      let acc = 0, seg = 1;
      while (seg < tl.stops.length - 1 && acc + tl.stops[seg].duration_ms < target) { acc += tl.stops[seg].duration_ms; seg += 1; }
      const dur = tl.stops[seg].duration_ms || 1;
      const local = Math.min(1, Math.max(0, (target - acc) / dur));
      const ease = local < 0.5 ? 2 * local * local : 1 - Math.pow(-2 * local + 2, 2) / 2;
      const from = tl.stops[seg - 1].positions || tl.base;
      const to = tl.stops[seg].positions || tl.base;
      Object.keys(tl.base).forEach((id) => {
        const a = from[id] || tl.base[id];
        const b = to[id] || a;
        out[id] = { x: a.x + (b.x - a.x) * ease, y: a.y + (b.y - a.y) * ease };
      });
    } else {
      tl.tracks.forEach((tr) => { out[tr.id] = samplePolyline(tr.points, Math.min(1, Math.max(0, t))); });
    }
    return out;
  };

  function stopAnim() {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    setPlaying(false);
    setAnimPos(null);
  }

  const playAnim = () => {
    const tl = buildTimeline();
    if (!tl) return;
    setPlaying(true);
    setSelectedIds([]);
    setActiveFrame(0);
    const startTs = performance.now();
    const tick = (ts) => {
      const t = Math.min(1, ((ts - startTs) * speed) / tl.total);
      setAnimPos(positionsAt(tl, t));
      if (t < 1) { rafRef.current = requestAnimationFrame(tick); return; }
      if (loop) {
        setTimeout(() => { if (rafRef.current) { rafRef.current = null; playAnim(); } }, 400);
        rafRef.current = -1;
        return;
      }
      setPlaying(false);
      setTimeout(() => setAnimPos(null), 600);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  /* ------- تصدير التحريك كصورة متحركة GIF أو مقطع فيديو WebM ------- */
  const [exportingAnim, setExportingAnim] = useState('');

  const captureAnimFrames = async (tl, fps) => {
    const count = Math.max(10, Math.min(120, Math.round((tl.total / 1000) * fps)));
    const stage = stageRef.current;
    const prevScale = { x: stage.scaleX(), y: stage.scaleY() };
    const prevPos = stage.position();
    const shots = [];
    for (let i = 0; i <= count; i += 1) {
      setAnimPos(positionsAt(tl, i / count));
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      stage.scale({ x: 1, y: 1 });
      stage.position({ x: 0, y: 0 });
      shots.push(stage.toCanvas({ pixelRatio: 1, width: bw, height: bh }));
      stage.scale(prevScale);
      stage.position(prevPos);
    }
    setAnimPos(null);
    return shots;
  };

  const exportAnimation = async (format) => {
    const tl = buildTimeline();
    if (!tl || exportingAnim) return;
    stopAnim();
    setExportingAnim(format);
    const fps = format === 'gif' ? 12 : 20;
    try {
      const shots = await captureAnimFrames(tl, fps);
      const outW = Math.min(bw, format === 'gif' ? 640 : 900);
      const outH = Math.round((outW * bh) / bw);
      const canvas = document.createElement('canvas');
      canvas.width = outW; canvas.height = outH;
      const ctx = canvas.getContext('2d');

      if (format === 'gif') {
        const { GIFEncoder, quantize, applyPalette } = await import('gifenc');
        const enc = GIFEncoder();
        const delay = Math.round(1000 / fps);
        for (const c of shots) {
          ctx.clearRect(0, 0, outW, outH);
          ctx.drawImage(c, 0, 0, outW, outH);
          const { data } = ctx.getImageData(0, 0, outW, outH);
          const palette = quantize(data, 256);
          const index = applyPalette(data, palette);
          enc.writeFrame(index, outW, outH, { palette, delay });
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, 0));
        }
        enc.finish();
        const blob = new Blob([enc.bytesView()], { type: 'image/gif' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'تمرين-متحرك.gif';
        a.click();
        toast.success('تم تحميل الصورة المتحركة GIF');
      } else {
        const stream = canvas.captureStream(fps);
        const mime = MediaRecorder.isTypeSupported('video/webm;codecs=vp9') ? 'video/webm;codecs=vp9' : 'video/webm';
        const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 4500000 });
        const chunks = [];
        rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
        const done = new Promise((resolve) => { rec.onstop = resolve; });
        rec.start();
        for (const c of shots) {
          ctx.fillStyle = '#0B0E14';
          ctx.fillRect(0, 0, outW, outH);
          ctx.drawImage(c, 0, 0, outW, outH);
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, 1000 / fps));
        }
        await new Promise((r) => setTimeout(r, 250));
        rec.stop();
        await done;
        const blob = new Blob(chunks, { type: 'video/webm' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'تمرين-متحرك.webm';
        a.click();
        toast.success('تم تحميل مقطع التمرين (WebM) — يعمل على المتصفحات وبرامج التحرير. للمشاركة في واتساب استخدم زر GIF');
      }
    } catch {
      toast.error('تعذّر تصدير التحريك، حاول مرة أخرى');
    }
    setExportingAnim('');
  };

  useEffect(() => () => { if (rafRef.current && rafRef.current > 0) cancelAnimationFrame(rafRef.current); }, []);

  const autosaveSkipRef = useRef(true);  useEffect(() => {
    if (autosaveSkipRef.current) { autosaveSkipRef.current = false; return; }
    if (readOnly) return;
    const t = setTimeout(() => { save(); }, 1200);
    return () => clearTimeout(t);
  }, [scene.elements]);

  React.useImperativeHandle(ref, () => ({ getPreviewImage: getDataUrl, getScene: () => scene, save }));

  const editingElement = scene.elements.find((e) => e.id === editingEl);
  const selectedEl = selectedIds.length === 1 ? scene.elements.find((e) => e.id === selectedIds[0]) : null;
  const teamColors = { a: scene.team_a_color || '#2563eb', b: scene.team_b_color || '#dc2626' };

  return (
    <div ref={rootRef} className={`relative flex flex-col overflow-hidden rounded-xl border border-white/10 bg-[#0B0E14] ${fullscreen ? 'fixed inset-0 z-[70] rounded-none' : ''}`} data-testid="pitch-editor-root" tabIndex={0}>
      <PitchToolbarTop
        readOnly={readOnly} onDelete={deleteSelected} hasSelection={selectedIds.length > 0}
        onLock={lockSelected} isLocked={!!selectedEl?.locked}
        fullscreen={fullscreen} onToggleFullscreen={toggleFullscreen}
        onSave={save} saveStatus={saveStatus}
        onExportPng={exportPng} onExportPdf={exportPdf} onPrint={handlePrint}
        onOpenSettings={() => setSettingsOpen(true)}
        onAutoFormation={handleAutoFormation}
      />
      <div ref={containerRef} className={`relative flex w-full items-center justify-center touch-none ${fullscreen ? 'flex-1' : ''}`} style={{ height: fullscreen ? undefined : 'min(80vh, 860px)', overflow: 'hidden' }} data-testid="pitch-canvas-container">
        <Stage
          ref={stageRef}
          width={containerSize.w}
          height={containerSize.h}
          scaleX={displayScale}
          scaleY={displayScale}
          x={0}
          y={0}
          draggable={false}
          onMouseDown={handleStageMouseDown}
          onMouseMove={handleStageMouseMove}
          onMouseUp={handleStageMouseUp}
          onTouchStart={handleStageMouseDown}
          onTouchMove={handleStageMouseMove}
          onTouchEnd={handleStageMouseUp}
        >
          <Layer>
            <FieldMarkings template={scene.field_template} orientation={scene.orientation} w={bw} h={bh} background={scene.field_background} />
          </Layer>
          <Layer>
            {scene.elements.map((el) => {
              const common = {
                ref: (node) => { if (node) nodeRefs.current[el.id] = node; },
                draggable: !readOnly && !el.locked && !playing,
                rotation: el.rotation || 0,
                onClick: (e) => {
                  if (readOnly) return;
                  const additive = e.evt?.shiftKey;
                  setSelectedIds((prev) => (additive ? (prev.includes(el.id) ? prev.filter((i) => i !== el.id) : [...prev, el.id]) : [el.id]));
                },
                onTap: () => { if (!readOnly) setSelectedIds([el.id]); },
                onDragMove: (e) => handleDragMove(el, e.target),
                onDragEnd: (e) => handleDragEnd(el, e.target),
                onDblClick: () => {
                  if (readOnly) return;
                  if (EDITABLE_TOKEN_TYPES.includes(el.type)) setEditingEl(el.id);
                },
                onDblTap: () => { if (!readOnly && EDITABLE_TOKEN_TYPES.includes(el.type)) setEditingEl(el.id); },
              };
              if (isToken(el.type)) {
                const ap = animPos?.[el.id];
                return <Group key={el.id} {...common} x={px(ap ? ap.x : el.x)} y={py(ap ? ap.y : el.y)}><TokenShape el={el} teamColors={teamColors} showNames={scene.show_names} /></Group>;
              }
              const style = PATH_STYLE[el.type] || {};
              if (style.circleShape) {
                return <Circle key={el.id} {...common} x={px(el.x)} y={py(el.y)} radius={px(el.radius || 3)} stroke={el.color} strokeWidth={el.strokeWidth} fill={el.type === 'zone_area' ? el.color : undefined} opacity={style.zone ? 0.25 : 1} />;
              }
              if (style.rectShape) {
                return <Rect key={el.id} {...common} x={px(el.x)} y={py(el.y)} width={px(el.width || 10)} height={py(el.height || 10)} stroke={el.color} strokeWidth={el.strokeWidth} fill={style.zone ? el.color : undefined} opacity={style.zone ? 0.25 : 1} />;
              }
              const pts = (el.points || []).map((p, i) => (i % 2 === 0 ? px(p) : py(p)));
              if (el.type === 'arrow' || el.type === 'curved_arrow' || el.type === 'movement_path' || el.type === 'pass_path' || el.type === 'dribble_path') {
                return (
                  <Arrow key={el.id} {...common} points={pts} stroke={el.color} fill={el.color} strokeWidth={el.strokeWidth} dash={style.dash ? [10, 5] : undefined}
                    tension={style.curved ? 0.5 : 0} pointerLength={10} pointerWidth={10} hitStrokeWidth={20} />
                );
              }
              return (
                <Line key={el.id} {...common} points={pts} stroke={el.color} strokeWidth={el.strokeWidth} dash={style.dash ? [8, 4] : undefined}
                  tension={style.free ? 0.3 : 0} lineCap="round" lineJoin="round" hitStrokeWidth={20} />
              );
            })}
            {drawingRef.current && drawingRef.current.points?.length >= 4 && (() => {
              const d = drawingRef.current;
              const dStyle = PATH_STYLE[d.type] || {};
              const dPts = d.points.map((p, i) => (i % 2 === 0 ? px(p) : py(p)));
              if (dStyle.circleShape || dStyle.rectShape) return null;
              if (['arrow', 'curved_arrow', 'movement_path', 'pass_path'].includes(d.type)) {
                return (
                  <Arrow points={dPts} stroke={color} fill={color} strokeWidth={strokeWidth} dash={dStyle.dash ? [10, 5] : undefined}
                    tension={dStyle.curved ? 0.5 : 0} pointerLength={10} pointerWidth={10} opacity={0.55} listening={false} />
                );
              }
              return (
                <Line points={dPts} stroke={color} strokeWidth={strokeWidth} dash={dStyle.dash ? [8, 4] : undefined}
                  tension={dStyle.free ? 0.3 : 0} lineCap="round" lineJoin="round" opacity={0.55} listening={false} />
              );
            })()}
            {!readOnly && selectedEl && ENDPOINT_EDITABLE.includes(selectedEl.type) && (
              <>
                {[0, 1].map((idx) => (
                  <Circle
                    key={idx}
                    x={px(selectedEl.points[idx * 2])} y={py(selectedEl.points[idx * 2 + 1])}
                    radius={9} fill="#0d9488" stroke="#fff" strokeWidth={2} draggable
                    onDragMove={(e) => {
                      const stage = stageRef.current;
                      const pos = e.target.getStage().getRelativePointerPosition();
                      const nx = clampPercent(toPercentX(pos.x)), ny = clampPercent(toPercentY(pos.y));
                      const pts = [...selectedEl.points];
                      pts[idx * 2] = nx; pts[idx * 2 + 1] = ny;
                      setScene((s) => ({ ...s, elements: s.elements.map((el) => (el.id === selectedEl.id ? { ...el, points: pts } : el)) }));
                    }}
                    onDragEnd={() => commitElements(scene.elements)}
                    data-testid={`pitch-path-handle-${idx}`}
                  />
                ))}
              </>
            )}
            {!readOnly && <Transformer ref={trRef} rotateEnabled resizeEnabled />}
          </Layer>
        </Stage>
        {!readOnly && (
          <PitchToolsDrawer
            open={drawerOpen} onClose={() => setDrawerOpen(false)} tool={tool}
            onInsertToken={(type) => insertElement(type, multiAdd)}
            onSelectPathTool={(t) => setTool(t)}
            hasSelection={selectedIds.length > 0}
            onDuplicate={duplicateSelected} onBringFront={bringFront} onSendBack={sendBack}
            onGroup={groupSelected} onUngroup={ungroupSelected}
          />
        )}
        <PitchSettingsSheet
          open={settingsOpen} onClose={() => setSettingsOpen(false)}
          fieldTemplate={scene.field_template} setFieldTemplate={(v) => setScene((s) => ({ ...s, field_template: v }))}
          orientation={scene.orientation} setOrientation={(v) => setScene((s) => ({ ...s, orientation: v }))}
          fieldBackground={scene.field_background || 'green'} setFieldBackground={(v) => setScene((s) => ({ ...s, field_background: v }))}
          teamColors={teamColors} setTeamColors={(fn) => setScene((s) => { const next = fn({ a: s.team_a_color, b: s.team_b_color }); return { ...s, team_a_color: next.a, team_b_color: next.b }; })}
          showNames={!!scene.show_names} setShowNames={(v) => setScene((s) => ({ ...s, show_names: v }))}
        />
      </div>
      {(!readOnly || canAnimate) && (
        <PitchAnimator
          readOnly={readOnly}
          frames={frames} activeFrame={activeFrame} onSelectFrame={selectFrame}
          onAddFrame={addFrame} onDeleteFrame={deleteFrame} onUpdateFrame={updateFrame}
          playing={playing} onPlay={playAnim} onStop={stopAnim}
          speed={speed} setSpeed={setSpeed} loop={loop} setLoop={setLoop}
          canAnimate={canAnimate}
          onExport={exportAnimation}
          exporting={exportingAnim}
          hint={readOnly
            ? `${frames.length || pathTracks.length} مشهد — اضغط تشغيل لمشاهدة حركة التمرين (انسخ التمرين لتعديله)`
            : frames.length
            ? `${frames.length} مشهد — اختر مشهداً لتعديل مواضعه، ثم اضغط تشغيل لمشاهدة الحركة`
            : (pathTracks.length
              ? `جاهز للتحريك تلقائياً: ${pathTracks.length} عنصر مرتبط بمسار مرسوم — اضغط تشغيل`
              : 'ارسم مسار حركة/تمريرة تبدأ عند لاعب أو كرة ليتحرك تلقائياً، أو أضف مشهداً وحرّك اللاعبين لمواضع جديدة')}
        />
      )}
      <PitchToolbarBottom
        readOnly={readOnly} onUndo={undo} onRedo={redo} canUndo={historyState.canUndo} canRedo={historyState.canRedo}
        color={color} setColor={setColor} strokeWidth={strokeWidth} setStrokeWidth={setStrokeWidth}
        snap={snap} setSnap={setSnap}
        drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen}
        statusText={
          PATH_STYLE[tool] && !PATH_STYLE[tool].circleShape && !PATH_STYLE[tool].rectShape
            ? `اسحب على الملعب لرسم: ${PATH_TOOLS.find((t) => t.type === tool)?.label || ''}`
            : `${scene.elements.length} عنصر`
        }
      />

      {editingElement && (
        <TokenEditModal
          el={editingElement}
          rosterPlayers={rosterPlayers}
          onClose={() => setEditingEl(null)}
          onSave={(patch) => { updateElement(editingElement.id, patch); setEditingEl(null); }}
          onCancelEmpty={() => { removeElement(editingElement.id); setEditingEl(null); }}
        />
      )}
    </div>
  );
});

export default PitchEditor;

function TokenEditModal({ el, rosterPlayers, onClose, onSave, onCancelEmpty }) {
  const isTextLike = el.type === 'text';
  const isNumberLike = el.type === 'number';
  const isPlayer = el.type === 'player_a' || el.type === 'player_b' || el.type === 'goalkeeper' || el.type === 'neutral';
  const [text, setText] = useState(el.text || '');
  const [playerName, setPlayerName] = useState(el.playerName || '');

  const handleSave = () => {
    if ((isTextLike) && !text.trim()) { onCancelEmpty(); return; }
    onSave({ text, playerName: isPlayer ? playerName : undefined });
  };

  return (
    <Modal open title={isTextLike ? 'تحرير النص' : isPlayer ? 'تحديد اللاعب' : 'تحرير الرقم'} onClose={() => { if (isTextLike && !text.trim()) onCancelEmpty(); else onClose(); }}>
      <div className="space-y-3">
        {isTextLike && (
          <textarea autoFocus value={text} onChange={(e) => setText(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" rows={3} placeholder="أدخل النص" data-testid="pitch-text-edit-input" />
        )}
        {(isNumberLike || isPlayer) && (
          <input autoFocus type="text" value={text} onChange={(e) => setText(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="الرقم" data-testid="pitch-number-edit-input" />
        )}
        {isPlayer && (
          <>
            <input type="text" value={playerName} onChange={(e) => setPlayerName(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="اسم اللاعب (اختياري)" data-testid="pitch-player-name-input" />
            {rosterPlayers && rosterPlayers.length > 0 && (
              <select
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                data-testid="pitch-roster-select"
                defaultValue=""
                onChange={(e) => {
                  const p = rosterPlayers.find((r) => r.id === e.target.value);
                  if (p) { setPlayerName(p.name); setText(p.jersey_number ? String(p.jersey_number) : text); }
                }}
              >
                <option value="">-- اختيار لاعب حقيقي من الفريق --</option>
                {rosterPlayers.map((p) => <option key={p.id} value={p.id}>{p.name} {p.jersey_number ? `#${p.jersey_number}` : ''}</option>)}
              </select>
            )}
          </>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm" data-testid="pitch-edit-cancel-btn">إلغاء</button>
          <button onClick={handleSave} className="rounded-lg bg-[#16233b] px-4 py-2 text-sm font-semibold text-white" data-testid="pitch-edit-save-btn">حفظ</button>
        </div>
      </div>
    </Modal>
  );
}
